// Week: this week and the next, per day the day type and what is on it; tap a day to see it whole (golf 4b).
import { useLiveQuery } from "dexie-react-hooks";
import { availabilityLine, useHousehold, useOthersTime, useShowPartner } from "../lib/samen";
import { ChevronRight } from "lucide-react";
import { pickTemplateId } from "@shared/rhythm";
import { dateLabel, datesOn } from "@shared/dates";
import { weekCount, weekStart, weekTarget, weeksInRow } from "@shared/routines";
import { addDays, displayTime, planningDate, timeToMinutes } from "@shared/time";
import { SyncBadge } from "../components/SyncBadge";
import { Button, cn } from "../components/ui";
import { useOccurrences } from "../lib/appointments";
import { db } from "../lib/db";
import { navigate } from "../lib/hooks";
import { isPausedIn, useRhythm } from "../lib/rhythm";
import { useCompletions, useRoutines } from "../lib/routines";
import type { Me } from "../lib/session";

const dayName = new Intl.DateTimeFormat("nl-NL", { weekday: "long", timeZone: "UTC" });
const dayNumber = new Intl.DateTimeFormat("nl-NL", { day: "numeric", month: "short", timeZone: "UTC" });
const asDate = (iso: string) => new Date(`${iso}T00:00:00Z`);

export function WeekScreen({ me }: { me: Me }) {
  const today = planningDate(new Date(), timeToMinutes(me.settings?.day_starts_at ?? "04:00"));
  const monday = weekStart(today);
  const last = addDays(monday, 13);
  const rhythm = useRhythm();
  const routines = useRoutines();
  const completions = useCompletions();
  const occurrences = useOccurrences(monday, last);
  const yearly = useLiveQuery(async () => (await db.important_dates.toArray()).filter((d) => !d.deleted_at));
  // Tsjits' busy and away times (B42), when the switch in Instellingen is on.
  const showPartner = useShowPartner();
  const others = useOthersTime(monday, last);
  const household = useHousehold();
  const nameOf = (id: string) => household?.find((h) => h.id === id)?.name ?? "De ander";
  const data = useLiveQuery(async () => {
    const plans = (await db.day_plans.where("plan_date").between(monday, last, true, true).toArray()).filter((p) => !p.deleted_at);
    const entries = (await db.plan_entries.where("plan_date").between(monday, last, true, true).toArray()).filter(
      (e) => !e.deleted_at && (e.status === "planned" || e.status === "done"),
    );
    return { plans, entries };
  }, [monday, last]);

  const isPaused = isPausedIn(rhythm);
  const counted = (routines ?? []).filter((r) => r.weekly_target);
  const weeks = [
    { title: "Deze week", start: monday },
    { title: "Volgende week", start: addDays(monday, 7) },
  ];

  return (
    <main className="mx-auto max-w-xl px-4 pt-[max(1.25rem,env(safe-area-inset-top))] pb-44">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-2">
        <h1 className="text-[1.75rem] font-bold">Week</h1>
        <SyncBadge />
      </header>
      <Button className="mb-6 w-full" onClick={() => navigate("/avond")}>
        Morgen klaarzetten
      </Button>
      {weeks.map((week) => (
        <section key={week.start} aria-labelledby={`week-${week.start}`} className="mb-8">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <h2 id={`week-${week.start}`} className="text-lg font-semibold">
              {week.title}
            </h2>
            {completions &&
              counted.map((r) => {
                // The gym streak (B39): weeks in a row with the goal reached.
                const streak = week.start === monday ? weeksInRow(r, completions, today, isPaused) : 0;
                return (
                  <span key={r.id} className="text-sm text-ink-soft tabular">
                    {r.title} {weekCount(r.id, completions, week.start)} van {weekTarget(r, week.start, isPaused)}
                    {streak > 1 ? `, ${streak} weken op rij` : ""}
                  </span>
                );
              })}
          </div>
          <ul className="divide-y divide-mist rounded-2xl border border-mist bg-surface">
            {Array.from({ length: 7 }, (_, i) => addDays(week.start, i)).map((date) => {
              const plan = data?.plans.find((p) => p.plan_date === date);
              const templateId = plan
                ? plan.template_id
                : rhythm
                  ? pickTemplateId(date, rhythm.templates, rhythm.rules, rhythm.overrides)
                  : null;
              const typeName = plan?.template_name ?? rhythm?.templates.find((t) => t.id === templateId)?.name ?? "";
              const dayEntries = data?.entries.filter((e) => e.plan_date === date) ?? [];
              const routinesOn = dayEntries.filter((e) => e.kind === "routine").map((e) => e.title);
              const taskCount = dayEntries.filter((e) => e.kind === "task").length;
              const appointments = (occurrences ?? []).filter((o) => o.date === date);
              const summary = [
                ...datesOn(yearly ?? [], date).today.map((d) => dateLabel(d, date)),
                ...appointments.map((o) => `${o.title} ${displayTime(o.startLocal.slice(11, 16))}`),
                ...routinesOn,
                ...(taskCount ? [taskCount === 1 ? "1 taak" : `${taskCount} taken`] : []),
              ];
              const isToday = date === today;
              return (
                <li key={date}>
                  <a
                    href={isToday ? "/" : `/dag/${date}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(isToday ? "/" : `/dag/${date}`);
                    }}
                    aria-current={isToday ? "date" : undefined}
                    // A day that is over is in a quieter background, not faded (contrast stays readable).
                    className={cn("flex min-h-16 items-center gap-3 px-3 py-2", date < today && "bg-paper")}
                  >
                    <span className="w-24 shrink-0">
                      <span className={cn("block font-semibold first-letter:uppercase", isToday && "text-pine")}>
                        {isToday ? "Vandaag" : dayName.format(asDate(date))}
                      </span>
                      <span className="block text-sm text-ink-soft">{dayNumber.format(asDate(date))}</span>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block [overflow-wrap:anywhere]">{typeName}</span>
                      {summary.length > 0 && (
                        <span className="block text-sm text-ink-soft [overflow-wrap:anywhere]">{summary.join(", ")}</span>
                      )}
                      {showPartner &&
                        (others ?? [])
                          .filter((a) => a.avail_date === date)
                          .map((a) => (
                            <span key={a.id} className="block text-sm text-pine [overflow-wrap:anywhere]">
                              {availabilityLine(a, nameOf(a.owner_id))}
                            </span>
                          ))}
                    </span>
                    <ChevronRight aria-hidden className="size-5 shrink-0 text-ink-soft" />
                  </a>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </main>
  );
}
